import React, { useState } from 'react';
import { toast } from 'sonner';
import { Workflow, Sparkles, RefreshCw, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AGENTS, REASON_CODES } from '@/data/seed';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/lib/usePageTitle';
import { useConfirm } from '@/components/shared/ConfirmDialog';

const SAMPLE_ASSIGNMENTS = [
  { case: 'CASE-10412', debtor: 'Fahad A.', amount: 18450, score: 86, agent: 'AGT-014', agentName: 'Layla Bin Ali', reasons: ['HIGH_VALUE_CASE', 'ARABIC_LANGUAGE_MATCH', 'AGENT_HAS_CAPACITY', 'CLIENT_PRIORITY_A'] },
  { case: 'CASE-10413', debtor: 'Mona K.', amount: 9820, score: 74, agent: 'AGT-016', agentName: 'Noura Al-Dosari', reasons: ['PAYMENT_PROPENSITY_HIGH', 'RIYADH_BRANCH_MATCH', 'PTP_DUE_TODAY'] },
  { case: 'CASE-10414', debtor: 'Ahmed B.', amount: 41200, score: 92, agent: 'AGT-011', agentName: 'Fares Sayer', reasons: ['HIGH_VALUE_CASE', 'LEGAL_RISK_LOW', 'AGENT_HAS_CAPACITY'] },
  { case: 'CASE-10415', debtor: 'Reem S.', amount: 5640, score: 58, agent: 'AGT-012', agentName: 'Sara Al-Qahtani', reasons: ['ARABIC_LANGUAGE_MATCH', 'AGENT_HAS_CAPACITY'] },
  { case: 'CASE-10416', debtor: 'Khalid Y.', amount: 24300, score: 81, agent: 'AGT-015', agentName: 'Khalid Al-Harbi', reasons: ['HIGH_VALUE_CASE', 'CLIENT_PRIORITY_A', 'AGENT_HAS_CAPACITY'] },
  { case: 'CASE-10417', debtor: 'Sara F.', amount: 7200, score: 65, agent: 'AGT-013', agentName: 'Mohammed Al-Otaibi', reasons: ['PAYMENT_PROPENSITY_HIGH', 'AGENT_HAS_CAPACITY'] },
];

const WEIGHTS = [
  { id: 'amount', label: 'Amount', value: 25 },
  { id: 'age', label: 'Delinquency age', value: 20 },
  { id: 'propensity', label: 'Payment propensity', value: 15 },
  { id: 'contact', label: 'Contactability', value: 15 },
  { id: 'client', label: 'Client priority', value: 10 },
  { id: 'promise', label: 'Promise risk', value: 10 },
  { id: 'legal', label: 'Legal readiness', value: 5 },
];

export default function Assignments() {
  usePageTitle('Decisioning Studio');
  const navigate = useNavigate();
  const { pushEvent } = useApp();
  const confirm = useConfirm();
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const run = async () => {
    const ok = await confirm({
      title: 'Run assignment engine?',
      description: 'This will redistribute pending cases across all 6 agents based on the current priority weights. Existing assignments will be overwritten and notified.',
      impactSummary: [
        { label: 'Cases to assign', value: '142' },
        { label: 'Agents impacted', value: '6' },
        { label: 'Avg score', value: '78' },
        { label: 'Reversible', value: 'Yes — re-run to revert' },
      ],
      confirmLabel: 'Run assignment',
      tone: 'warning',
    });
    if (!ok) return;
    setRunning(true); setDone(false);
    setTimeout(() => {
      setRunning(false); setDone(true);
      toast.success('Assignment complete', { description: '142 cases distributed across 6 agents' });
      pushEvent({ type: 'AssignmentRun', src: 'Assignment Engine', dst: 'Agent Queue', status: 'success', payload: '142 cases assigned' });
    }, 1300);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Decisioning"
        title="Automatic Assignment Engine"
        subtitle="Transparent, weighted priority scoring distributes cases to agents based on skill, capacity, branch, language and client tier — with explainable reason codes."
        actions={(
          <>
            <Button variant="outline" size="sm"><Workflow className="h-4 w-4 mr-1.5" />Edit rules</Button>
            <Button size="sm" onClick={run} disabled={running} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {running ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Running…</> : <><Sparkles className="h-4 w-4 mr-1.5" />Run assignment</>}
            </Button>
          </>
        )}
      />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Weights */}
        <div className="surface-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-primary">Priority score formula</p>
          <h3 className="text-base font-semibold mt-0.5 mb-3">Weighted decision model</h3>
          <div className="space-y-3">
            {WEIGHTS.map((w) => (
              <div key={w.id}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{w.label}</span>
                  <span className="font-mono-num font-semibold">{w.value}%</span>
                </div>
                <Progress value={w.value * 4} className="h-1.5" />
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground">
            Total weight 100% • Tune in Admin → Automation rules
          </div>
        </div>

        {/* Agent workload */}
        <div className="surface-card p-5 lg:col-span-2">
          <p className="text-[10px] uppercase tracking-widest text-primary">Agent capacity</p>
          <h3 className="text-base font-semibold mt-0.5 mb-3">Workload & headroom across team</h3>
          <div className="space-y-3">
            {AGENTS.map((a) => {
              const util = (a.activeCases / a.capacity) * 100;
              const tone = util > 90 ? 'destructive' : util > 75 ? 'warning' : 'success';
              return (
                <div key={a.id} className="grid grid-cols-12 items-center gap-3 text-sm">
                  <div className="col-span-3 truncate">
                    <p className="font-medium">{a.name}</p>
                    <p className="text-[11px] text-muted-foreground">{a.skill} • {a.branch}</p>
                  </div>
                  <div className="col-span-7">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${tone === 'destructive' ? 'bg-destructive' : tone === 'warning' ? 'bg-warning' : 'bg-success'}`} style={{ width: `${util}%` }} />
                      </div>
                      <span className="text-[11px] font-mono-num text-muted-foreground w-14 text-right">{a.activeCases}/{a.capacity}</span>
                    </div>
                  </div>
                  <div className="col-span-2 text-right text-[11px] text-muted-foreground">
                    PTP today <span className="font-mono-num font-semibold text-foreground">{a.ptpToday}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Result table */}
      <div className="surface-card overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Last assignment run</p>
            <h3 className="text-base font-semibold mt-0.5">Explainable distribution — sample cases</h3>
          </div>
          {done ? <Badge className="bg-success/15 text-success border-success/40" variant="outline">142 assigned • just now</Badge> : <Badge variant="outline">Pending run</Badge>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th scope="col" className="text-left px-5 py-2.5 font-medium">Case</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Debtor</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Amount</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Priority score</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Assigned agent</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Why</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {SAMPLE_ASSIGNMENTS.map((a, idx) => (
                  <motion.tr key={a.case}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: done ? 1 : 0.5, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    className="border-t border-border hover:bg-muted/40"
                  >
                    <td className="px-5 py-3 font-medium">{a.case}</td>
                    <td className="px-3 py-3 text-muted-foreground">{a.debtor}</td>
                    <td className="px-3 py-3 text-right font-mono-num">SAR {a.amount.toLocaleString()}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${a.score}%` }} />
                        </div>
                        <span className="font-mono-num text-xs">{a.score}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-full bg-primary/15 text-primary text-[10px] font-semibold flex items-center justify-center">{a.agentName.split(' ').map((w) => w[0]).join('')}</div>
                        <span className="text-xs font-medium">{a.agentName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {a.reasons.map((r) => (
                          <Badge key={r} variant="outline" className="text-[10px] bg-primary/8 border-primary/30 text-gold-deep" title={REASON_CODES[r] || r}>{r}</Badge>
                        ))}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-border flex justify-between items-center">
          <p className="text-xs text-muted-foreground">Showing 6 of 142 assigned cases</p>
          <Button size="sm" variant="outline" onClick={() => navigate('/app/agent')}>Open agent workspace <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
      </div>
    </div>
  );
}
